/**
 * Which project a new thread will run in.
 *
 * Extracted from `useDraftProjectChoice` so the resolution stages own their
 * imports instead of sharing the orchestrator's. Five steps that only mean
 * something in order: the user's pick, or the default if they have not picked;
 * that summary matched against connected projects; the cloud row behind it when
 * it is a cloud project; and finally the presentation the composer shows. Every
 * stage is nullable on purpose — a chosen project may not be connected, and a
 * connected project may have no cloud row.
 */
import { useMemo } from "react";
import { usePublishBreadcrumbParent } from "../breadcrumb/publishedBreadcrumbParent";
import { resolveNewThreadBreadcrumb } from "./newThreadBreadcrumb";
import { useProjectIconResolver } from "../projects/useProjectIconResolver";

import { useCmmdForgeRuntimeListSnapshot } from "../../cmmd/cmmdForgeRuntimeListResource";
import type { Project } from "../../types";
import { useUiStateStore } from "../../uiStateStore";
import { useProjectDisplayNameResolver } from "../projects/useProjectDisplayNameResolver";
import { selectCloudCatalogRowForProject } from "./cloudCatalogRowSelection";
import {
  projectSelectionKey,
  resolveCloudSummaryForSelection,
  resolveConnectedProjectForSummary,
  resolveDefaultProject,
  resolveProjectForSelection,
  type ProjectSummary,
} from "./NewThreadLanding.logic";
import { resolveActiveProjectPresentation } from "./activeProjectPresentation";

export function useDraftProjectResolution({
  cloudCatalog,
  filteredProjectSummaries,
  lastActiveProject,
  projectOrder,
  projectSummaries,
  projects,
  selectedProjectKey,
}: {
  readonly cloudCatalog: ReturnType<typeof useCmmdForgeRuntimeListSnapshot>;
  readonly filteredProjectSummaries: ProjectSummary[];
  readonly lastActiveProject: ReturnType<typeof useUiStateStore.getState>["lastActiveProject"];
  readonly projectOrder: ReturnType<typeof useUiStateStore.getState>["projectOrder"];
  readonly projectSummaries: ProjectSummary[];
  readonly projects: readonly Project[];
  readonly selectedProjectKey: string | null;
}) {
  const activeProjectKey = useMemo(
    () =>
      lastActiveProject
        ? projectSelectionKey({
            environmentId: lastActiveProject.environmentId,
            id: lastActiveProject.projectId,
          })
        : null,
    [lastActiveProject],
  );

  const defaultSummary = useMemo(
    () => resolveDefaultProject(filteredProjectSummaries, projectOrder, activeProjectKey),
    [activeProjectKey, filteredProjectSummaries, projectOrder],
  );

  const selectedProjectSummary = useMemo<ProjectSummary | null>(
    () => resolveProjectForSelection(projectSummaries, selectedProjectKey, defaultSummary),
    [defaultSummary, projectSummaries, selectedProjectKey],
  );

  const activeProject = useMemo<Project | null>(
    () => resolveConnectedProjectForSummary(projects, selectedProjectSummary),
    [projects, selectedProjectSummary],
  );

  // The hero trigger and the chip read this presentation, so the name a person
  // chose in Forge has to win here the same way it does on a sidebar row.
  const resolveProjectDisplayName = useProjectDisplayNameResolver();

  // For stopped cloud catalog projects (not yet connected), activeProject will
  // be null even though the user intentionally selected a cloud catalog row.
  // Fall back to the matching ProjectSummary so the heading, chip, and send-
  // button state reflect the selection correctly.
  const activeCloudSummary = useMemo<ProjectSummary | null>(
    () =>
      resolveCloudSummaryForSelection({
        activeProjectPresent: activeProject !== null,
        projectSummaries,
        selectedProjectKey,
        defaultProject: defaultSummary,
      }),
    [activeProject, defaultSummary, projectSummaries, selectedProjectKey],
  );
  const activeProjectPresentation = useMemo(
    () =>
      resolveActiveProjectPresentation(activeProject, activeCloudSummary, {
        resolveDisplayName: resolveProjectDisplayName,
      }),
    [activeCloudSummary, activeProject, resolveProjectDisplayName],
  );
  const activeCloudCatalogRow = useMemo(
    () =>
      selectCloudCatalogRowForProject(cloudCatalog.rows, activeProjectPresentation, activeProject),
    [activeProject, activeProjectPresentation, cloudCatalog.rows],
  );

  const resolveProjectIcon = useProjectIconResolver();
  usePublishBreadcrumbParent(
    "/new",
    resolveNewThreadBreadcrumb(
      activeProject,
      activeProjectPresentation,
      activeCloudCatalogRow,
      resolveProjectIcon(
        activeProjectPresentation.environmentId,
        selectedProjectSummary?.resolvedProjectId ?? selectedProjectSummary?.id,
      ),
    ),
  );
  return { activeProject, activeProjectPresentation, activeCloudCatalogRow };
}
